/**
 * Maps an organizer-message API error (create/edit/delete) to a translated,
 * user-facing string.
 *
 * Extracted so OrganizerMessageModal.vue (save/edit) and
 * organizerMessagesMixin.js (delete) share the exact same error-code
 * mapping and the same caller-supplied fallback instead of each keeping its
 * own copy -- a failed DELETE must say "could not remove", never "could not
 * save", and vice versa.
 *
 * `t` is the caller's translation function (components pass `this.t`), kept
 * as a parameter so this stays a plain, testable function with no Vue/store
 * dependency of its own.
 */

function mapOrganizerMessageError( error, t, fallbackKey ) {
  const status = error.response && error.response.status;
  const code = error.response && error.response.data && error.response.data.error;
  const map = {
    empty_body: 'organizerMessageErrorEmptyBody',
    body_too_long: 'organizerMessageErrorBodyTooLong',
    invalid_send_time: 'organizerMessageErrorInvalidSendTime',
    invalid_target_date: 'organizerMessageErrorInvalidTargetDate',
    date_outside_window: 'organizerMessageErrorDateOutsideWindow',
    invalid_timezone: 'organizerMessageErrorInvalidTimezone',
    forbidden: 'organizerMessageErrorForbidden',
    not_found: 'organizerMessageErrorNotFound',
    // Recipient codes. Kept in this shared map so a failed DELETE or load
    // gets the same words as a failed save.
    manager_whatsapp_not_linked: 'organizerRecipientNotLinkedError',
    // Its own string, not the <small> hint under the disabled select: a key
    // doing double duty as hint and error gets reworded for one and breaks
    // the other.
    recipient_type_immutable: 'organizerRecipientFixedError',
    // 403: the requester is not the manager this message would be addressed
    // to. Its own code rather than the generic `forbidden` precisely so this
    // can say something true -- the trip's manager is someone else -- instead
    // of "you are not allowed to manage this trip's messages", which is not
    // what happened.
    not_the_ride_manager: 'organizerRecipientNotManagerError'
  };
  if ( code && map[code] ) return t( map[code] );
  if ( status === 403 ) return t( 'organizerMessageErrorForbidden' );
  if ( status === 404 ) return t( 'organizerMessageErrorNotFound' );
  // Caller-supplied fallback: a failed DELETE must not tell the manager
  // their message could not be SAVED. organizerMessageDeleteError existed
  // for this and was never wired up.
  return t( fallbackKey || 'organizerMessageSaveError' );
}

// CommonJS, matching src/utils/organizerMessagePayload.js: webpack consumes
// it from the components, and tests/unit can require it under plain node
// without a transpile step.
module.exports = { mapOrganizerMessageError };
